// library imports
import { useEffect, useRef } from "react";
import { Chart } from "@antv/g2";
import { uniqueId } from "@antv/util";
import { theme, Empty } from "antd";
import { isEqual } from "lodash";

export default function CustomGroupedColumnChart({
  data = [],
  xAxis = "date",
  yAxis = "value",
  colorKey = "type",
  xAxisTitle = "Date",
  yAxisTitle = "Value",
  domain = [],
  range = [],
}) {
  const { token } = theme.useToken();
  const containerRef = useRef(uniqueId("grouped-col-"));
  const container = containerRef.current;
  const chartRef = useRef(null);

  const prevDataRef = useRef([]);

  useEffect(() => {
    chartRef.current?.chart?.clear();

    const chartContainer = document.getElementById(container);
    chartContainer.innerHTML = "";

    const chart = new Chart({
      container: container,
      autoFit: true,
    });

    if (domain.length > 0 && range.length > 0) {
      chart.scale("color", { domain, range });
    }

    const interval = chart
      .interval()
      .data(data)
      .encode("x", xAxis)
      .encode("y", yAxis)
      .encode("color", colorKey)
      .transform({ type: "dodgeX" })
      .tooltip((d) => ({
        name: d[colorKey],
        value: d[yAxis],
      }));

    chart.axis("x", {
      line: true,
      lineStroke: token.colorTextBase,
      tickStroke: token.colorTextBase,
      title: xAxisTitle,
      titleFill: token.colorTextBase,
      labelFill: token.colorTextBase,
    });

    chart.axis("y", {
      line: true,
      lineStroke: token.colorTextBase,
      tickStroke: token.colorTextBase,
      title: yAxisTitle,
      titleFill: token.colorTextBase,
      labelFill: token.colorTextBase,
    });

    chart.legend({
      color: {
        itemLabelFill: token.colorTextBase,
      },
    });

    chart.interaction("tooltip", {
      render: (event, { title, items }) => {
        const normalizedTitle = Array.isArray(title) ? title[0] : title;
        return `<div style="width:150px;">
        <h3 style="padding:0;margin:0">${normalizedTitle}</h3>
        <ul style="padding: 0; margin: 0; list-style: none;">
          ${items
            .map((d) => {
              const formattedValue =
                d.value != null
                  ? Number(d.value).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  : d.value;
              return `
                <li style="display: flex; justify-content: space-between; align-items: center; margin: 4px 0;">
                  <span style="display: inline-flex; align-items: center;">
                    <span style="width: 8px; height: 8px; background-color: ${d.color}; border-radius: 50%; margin-right: 8px;"></span>
                    <span>${d.name}</span>
                  </span>
                  <span>${formattedValue}</span>
                </li>`;
            })
            .join("")}
        </ul>
      </div>`;
      },
    });

    chart.render();

    chartRef.current = { chart, interval };
    prevDataRef.current = data;

    return () => {
      chartRef.current?.chart?.destroy();
    };
  }, [token, xAxis, yAxis, colorKey, domain, range]);

  useEffect(() => {
    if (!isEqual(prevDataRef.current, data)) {
      chartRef.current?.interval?.changeData(data);
      prevDataRef.current = data;
    }
  }, [data]);

  useEffect(() => {
    const chartContainer = document.getElementById(container);
    const resizeObserver = new ResizeObserver(() => {});
    resizeObserver.observe(chartContainer);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const hasData = data.some((d) => d[yAxis] != null);

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      <div
        style={{
          width: "100%",
          height: "100%",
          visibility: hasData ? "visible" : "hidden",
        }}
        id={container}
      />
      {!hasData && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Empty />
        </div>
      )}
    </div>
  );
}
